import React, { useEffect, useState } from 'react'
import { FaImage } from 'react-icons/fa'

interface LoadableImageProps {
  src?: string
  alt?: string
  className?: string
  style?: React.CSSProperties
  draggable?: boolean
}

/**
 * <img> with explicit load/error states. Shows a pulsing skeleton until the image decodes and a
 * neutral icon placeholder on error — never the raw alt text of a broken/half-loaded image (a
 * recurring complaint on the AI-effect previews). The parent element MUST be positioned
 * (relative/absolute), because the skeleton + error overlays fill it via `inset-0`.
 */
const LoadableImage: React.FC<LoadableImageProps> = ({
  src,
  alt = '',
  className = '',
  style,
  draggable,
}) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading')

  // Reset whenever the source changes (e.g. before → AI output in the slider).
  useEffect(() => {
    setStatus(src ? 'loading' : 'error')
  }, [src])

  return (
    <>
      {src ? (
        <img
          src={src}
          alt={alt}
          style={style}
          draggable={draggable}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
          className={`${className} transition-opacity duration-200 ${
            status === 'loaded' ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ) : null}
      {status === 'loading' ? (
        <div className="pointer-events-none absolute inset-0 animate-pulse bg-black/10" aria-hidden />
      ) : null}
      {status === 'error' ? (
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/[0.04]"
          aria-hidden
        >
          <FaImage className="text-2xl text-black/20" />
        </div>
      ) : null}
    </>
  )
}

export default LoadableImage
